// web/src/lib/agentConfig.ts — Display metadata for agents.
//
// Frontend mirror of agent display data. The canonical source for `label`
// is each agent's spec.yaml `identity_core.display_label` (resolved
// server-side via SpecLoader). The backend includes the resolved label
// on every SpecialistBlock.label field — so the runtime UI is already
// driven by the spec. This file is consulted only for icons/colors and
// as a fallback label when an agent_id is rendered outside a
// SpecialistBlock context (e.g. AgentChip in the side panel before a
// turn completes).
//
// When you add or rename an agent:
//   1. Edit `agents/{name}/spec.yaml` — `identity_core.display_label`.
//   2. Mirror the new label below so chips/menus stay in sync.
//   3. (Eventually) collapse to an /api/agents endpoint and remove
//      this mirror entirely.
export const AGENT_CONFIG = {
    luna: { icon: "✦", color: "text-violet-400", label: "Luna", tools: [] },
    ptolemy: { icon: "◆", color: "text-indigo-400", label: "Portfolio Analysis", tools: ["portfolio_advisor", "market_data", "news_search"] },
    vault: { icon: "◈", color: "text-teal-400", label: "Tax Analysis", tools: ["tax_analysis"] },
    orion: { icon: "◎", color: "text-blue-400", label: "Risk Analysis", tools: ["portfolio_advisor", "trade_signals", "macro_data", "fx_data", "news_search", "alt_data"] },
    argus: { icon: "◉", color: "text-violet-400", label: "Monitoring", tools: [] },
    compass: { icon: "◇", color: "text-orange-400", label: "Cash Flow & Debt", tools: ["cash_flow_analysis"] },
    aura: { icon: "❋", color: "text-pink-400", label: "Retirement Planning", tools: ["retirement_analysis"] },
    talos: { icon: "◐", color: "text-cyan-400", label: "Execution", tools: [] },
    shield: { icon: "⬡", color: "text-lime-400", label: "Insurance Analysis", tools: ["insurance_analysis"] },
    herald: { icon: "◈", color: "text-yellow-400", label: "News & Macro", tools: ["news_search", "macro_data"] },
};
/** Icon for an agent ID. Falls back to the default if the agent is unknown. */
export const DEFAULT_AGENT_ICON = "◆";
/** Tailwind color class for an agent ID. Falls back to the default if unknown. */
export const DEFAULT_AGENT_COLOR = "text-slate-400";
/** The primary conversational agent that acts as the default entry point. */
export const DEFAULT_AGENT_ID = "luna";
// Re-export so components can import both agent config and sophistication
// gating from a single source without needing displayConfig directly.
export { SOPHISTICATION_THRESHOLD } from "./displayConfig";
/** Convenience accessor: icon for a given agent ID. */
export function agentIcon(agentId) {
    return AGENT_CONFIG[agentId]?.icon ?? DEFAULT_AGENT_ICON;
}
/** Convenience accessor: Tailwind color class for a given agent ID. */
export function agentColor(agentId) {
    return AGENT_CONFIG[agentId]?.color ?? DEFAULT_AGENT_COLOR;
}
/** Convenience accessor: tool list for a given agent ID. */
export function agentTools(agentId) {
    return AGENT_CONFIG[agentId]?.tools ?? [];
}
/** User-facing domain label for an agent ID. Custom user agents
    ("user/{uid}/{name}") show the slug component; unknown core
    agents fall back to title-cased agent_id. */
export function agentLabel(agentId) {
    if (agentId.startsWith("user/")) {
        const parts = agentId.split("/");
        return parts[parts.length - 1] ?? agentId;
    }
    return AGENT_CONFIG[agentId]?.label
        ?? agentId.charAt(0).toUpperCase() + agentId.slice(1);
}
